import React, { useEffect, useRef, useState, useCallback } from "react";
import { debounce } from "lodash";
import {
  PdfViewerComponent,
  Toolbar,
  Magnification,
  Navigation,
  // LinkAnnotation,
  BookmarkView,
  ThumbnailView,
  Print,
  TextSelection,
  // Annotation,
  TextSearch,
  FormFields,
  FormDesigner,
  Inject,
} from "@syncfusion/ej2-react-pdfviewer";
import { StrictErrorBoundary } from "../../../../../../helper/shared/strictErrorBoundary";
import { logError } from "../../../../../../utils/AppInsights";

const PdfViewer = ({ pageNo, pdfUrl }) => {
  const viewerRef = useRef(null);
  const [loadError, setLoadError] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(pdfUrl);
  const [key, setKey] = useState(0);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);

  const navigationTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const originalConsoleErrorRef = useRef(console.error);
  const mutationObserverRef = useRef(null);

  // safe state setter
  const safeSetState = useCallback((setter, value) => {
    if (isMountedRef.current) setter(value);
  }, []);

  // debounced navigation to avoid rapid calls
  const debouncedNavigate = useCallback(
    debounce((viewer, targetPage) => {
      try {
        if (
          viewer &&
          viewer.viewerBase &&
          typeof viewer.viewerBase.pageCount === "number" &&
          targetPage > 0 &&
          targetPage <= viewer.viewerBase.pageCount
        ) {
          viewer.navigation.goToPage(targetPage);
        }
      } catch (err) {
        logError("Navigation error: " + err);
      }
    }, 300),
    []
  );

  // reset when pdfUrl changes
  useEffect(() => {
    if (pdfUrl !== currentPdfUrl) {
      setCurrentPdfUrl(pdfUrl);
      setLoadError(false);
      setIsDocumentLoaded(false);
      setKey((prev) => prev + 1);
    }
  }, [pdfUrl, currentPdfUrl]);

  // navigate when ready
  useEffect(() => {
    const viewer = viewerRef.current;
    if (isDocumentLoaded && viewer) {
      debouncedNavigate(viewer, pageNo);
    }
    return () => debouncedNavigate.cancel();
  }, [pageNo, isDocumentLoaded, debouncedNavigate]);

  // watchdog that triggers fallback if pages never render
  useEffect(() => {
    let watchdog;
    if (!isDocumentLoaded) {
      watchdog = setTimeout(() => {
        const viewer = viewerRef.current;
        if (!viewer?.viewerBase?.pageCount) {
          logError("PDF Watchdog: No pages rendered. Triggering fallback.");
          // safeSetState(setLoadError, true);
        }
      }, 10000); // 10s
    }
    return () => clearTimeout(watchdog);
  }, [isDocumentLoaded, safeSetState]);

  // document load handler
  const handleDocumentLoad = () => {
    safeSetState(setIsDocumentLoaded, true);
    const viewer = viewerRef.current;

    if (viewer && pageNo > 0) {
      if (navigationTimeoutRef.current)
        clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = setTimeout(() => {
        try {
          if (viewer.viewerBase && pageNo <= viewer.viewerBase.pageCount) {
            viewer.navigation.goToPage(pageNo);
          }
        } catch (err) {
          logError("Initial navigation failed: " + err);
        }
      }, 500);
    }
  };

  const handleDocumentLoadFailed = (args) => {
    logError("Document load failed: " + JSON.stringify(args));
    safeSetState(setLoadError, true);
  };

  const handleAjaxFailure = (args) => {
    logError("AJAX failure during PDF load: " + JSON.stringify(args));
    safeSetState(setLoadError, true);
  };

  // Strong suppression: override console.error, window error handlers, and remove overlay DOM nodes
  useEffect(() => {
    isMountedRef.current = true;

    // Patterns to suppress visually; we still log them via logError
    const suppressPatterns = [
      "Server failed to authenticate",
      "certificate",
      "CERTIFICATE_VERIFY_FAILED",
      "Failed to fetch",
      "NetworkError",
      "CORS",
      "Load failed",
      "PDF",
      "pdfviewer",
      "pdfium",
      "pdf.js",
      "Worker",
    ];

    // override console.error to avoid React dev overlay + noisy library messages
    originalConsoleErrorRef.current = console.error;
    console.error = function (...args) {
      try {
        const joined = args
          .map((a) => (typeof a === "string" ? a : JSON.stringify(a)))
          .join(" ");
        if (suppressPatterns.some((p) => joined.includes(p))) {
          logError("Suppressed console.error: " + joined);
          return; // swallow
        }
      } catch (e) {
        // fall through to original
      }
      originalConsoleErrorRef.current.apply(console, args);
    };

    // global handlers to stop default overlay
    const globalHandler = (event) => {
      const err = event?.error || event?.reason || "";
      const message = typeof err === "string" ? err : err?.message || "";
      if (suppressPatterns.some((p) => message.includes(p))) {
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
        logError("Suppressed global error: " + message);
        safeSetState(setLoadError, true);
        return;
      }
    };

    window.addEventListener("error", globalHandler, true);
    window.addEventListener("unhandledrejection", globalHandler, true);

    // MutationObserver: remove injected big overlays or error modals
    try {
      mutationObserverRef.current = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            try {
              if (!(node instanceof HTMLElement)) return;
              const text = (
                node.innerText ||
                node.textContent ||
                ""
              ).toLowerCase();
              const style = window.getComputedStyle(node);
              const zIndex = parseInt(style?.zIndex || "0", 10);

              // Heuristics: remove nodes that look like error overlays (large z-index + contains 'error'/'failed')
              if (
                zIndex > 1000000 ||
                /(^|\s)error($|\s)/i.test(text) ||
                text.includes("server failed to authenticate") ||
                text.includes("certificate") ||
                text.includes("failed to fetch") ||
                text.includes("cors")
              ) {
                logError(
                  "Removing intrusive overlay node: " +
                    (node.innerText || node.textContent)
                );
                node.remove();
                return;
              }

              // Some overlays are nested; inspect children
              const childText = (
                node.innerText ||
                node.textContent ||
                ""
              ).toLowerCase();
              if (childText.includes("error") && zIndex > 1000) {
                logError("Removing nested overlay: " + childText.slice(0, 200));
                node.remove();
              }
            } catch (e) {
              // keep going
            }
          });
        });
      });

      // start observing body for additions
      if (document?.body) {
        mutationObserverRef.current.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    } catch (err) {
      logError("MutationObserver failed to initialize: " + err);
    }

    return () => {
      isMountedRef.current = false;

      // restore console
      if (originalConsoleErrorRef.current) {
        console.error = originalConsoleErrorRef.current;
      }

      window.removeEventListener("error", globalHandler, true);
      window.removeEventListener("unhandledrejection", globalHandler, true);

      if (mutationObserverRef.current) {
        try {
          mutationObserverRef.current.disconnect();
        } catch (e) {
          // ignore
        }
      }

      // cleanup viewer and timers
      if (navigationTimeoutRef.current)
        clearTimeout(navigationTimeoutRef.current);
      debouncedNavigate.cancel();
      if (viewerRef.current?.destroy) {
        try {
          viewerRef.current.destroy();
        } catch (e) {
          logError("PDF destroy error: " + e);
        }
      }
    };
  }, [debouncedNavigate, safeSetState]);

  // Retry/fallback UI
  if (loadError) {
    return (
      <div
        style={{
          width: "100%",
          height: "500px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
        }}
      >
        <div style={{ textAlign: "center", color: "#6c757d" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📄</div>
          <div>
            Oops! Something went wrong while loading the PDF Viewer. Please
            refresh and try again.
          </div>
          <button
            onClick={() => {
              setLoadError(false);
              setKey((prev) => prev + 1);
            }}
            style={{
              marginTop: "16px",
              padding: "8px 16px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // final render
  return (
    <StrictErrorBoundary
      fallback={<div className="fallback-display">PDF unavailable</div>}
    >
      <div key={`pdf-wrapper-${key}`} className="control-section">
        <PdfViewerComponent
          key={key}
          id={`pdf-viewer-${key}`}
          ref={viewerRef}
          documentPath={currentPdfUrl}
          // make sure to update this CDN version to match your installed package
          resourceUrl="https://cdn.syncfusion.com/ej2/31.2.15/dist/ej2-pdfviewer-lib"
          documentLoad={handleDocumentLoad}
          documentLoadFailed={handleDocumentLoadFailed}
          ajaxRequestFailure={handleAjaxFailure}
          enableNavigation={true}
          enableTextSelection={true}
          enableAnnotation={false}
          enableTextMarkupAnnotation={false}
          enableShapeAnnotation={false}
          enableStampAnnotation={false}
          style={{ height: "100%", width: "100%" }}
        >
          <Inject
            services={[
              Toolbar,
              Magnification,
              Navigation,
              // Annotation,
              // LinkAnnotation,
              BookmarkView,
              ThumbnailView,
              Print,
              TextSelection,
              TextSearch,
              FormFields,
              FormDesigner,
            ]}
          />
        </PdfViewerComponent>
      </div>
    </StrictErrorBoundary>
  );
};

export default PdfViewer;